import { React } from 'Imports';
import { useState, useEffect, useRef, useCallback } from 'react';
import { cx } from '$Utilities/cssHelpers';
import { AppBar, TextField, InputAdornment, Button } from 'MaterialUIComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/pro-solid-svg-icons';

import { SharedTabs, SharedTab } from '$Components/Shared/SharedTabs/SharedTabs';

import { PageHeader } from '$Components/Shared/PageHeader';
import { ConfigService } from '$State/ConfigFreezerService';
import { IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';

import { EventConfigurations } from './EventConfigurations/EventConfigurations';
import { EventConfigurations_SplitOFF } from './EventConfigurations_SplitOFF/EventConfigurations_SplitOFF';
import {
    getSplitUserAttributes,
    isSplitTreatmentOn,
    TypedSplitTreatments,
    splitTreatmentNames,
    useGetSplitTreatment,
} from '$Utilities/split-io-sdk';
import { ISplitTreatmentsChildProps } from '@splitsoftware/splitio-react/types/types';
import { EventNotificationsList } from './EventNotificationSettings/EventNotificationsList';
import { EventNotificationsListDescription } from './EventNotificationSettings/EventNotificationsListDescription';
import { SubscribersList } from './EventNotificationSettings/SubscribersList';
import { UserRolePermissions } from './UserSettings/UserRolePermissions';
import { VideoOverlayTimezone } from './VideoSettings/VideoOverlayTimezone';
import { RolesEnum, canAccessUserRolesPage } from 'externals/VerifyRole';
import { TermsOfUse } from '$Components/Shared/TermsOfUse';
import { ConfirmDialog } from '$Components/Shared/ConfirmDialog';
import type { Location } from 'react-router-dom';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import { isEncompass } from '$Utilities/integrationPlatform';
import { withNavigate, WithNavigateProps } from '$Components/hocs/withNavigate';
import { NavigationPrompt } from '$Components/Shared/NavigationPrompt';

const styles = require('./Settings.scss') as {
    appBar: string;
    main: string;
    mainGreyBackgroundColor: string;
    container: string;
    flexContainer: string;
    tabDisabled: string;
    tabIndicator: string;
    tabContentContainer: string;
    tabContentHidden: string;
    tabContentDisabled: string;
    tabHidden: string;
    tabBarRow: string;
    tabsWrapper: string;
    searchInputContainer: string;
    searchBarInput: string;
    inputAdornmentFilter: string;
    searchIcon: string;
    searchIconFocused: string;
    closeButtonSearchFilter: string;
    inputAdornmentCloseFilter: string;
    closeIcon: string;
    inputFocused: string;
    encompassInputRoot: string;
};

interface ISettingsProps extends WithNavigateProps {}

// Functional component to use the split hook
const SplitFeatureWrapper = (props: {
    onFeatureLoaded: (isVideoRetentionEnabled: boolean, isAutoScoreEnabled: boolean, isVideoRecallPreviewEnabled: boolean) => void;
}) => {
    const { getSplitTreatment } = useGetSplitTreatment();

    useEffect(() => {
        const checkSplit = async () => {
            try {
                const isVideoRetentionEnabled = await getSplitTreatment(splitTreatmentNames.videoRetention, getSplitUserAttributes());
                const isAutoScoreEnabled = await getSplitTreatment(splitTreatmentNames.autoScore, getSplitUserAttributes());
                const isVideoRecallPreviewEnabled = await getSplitTreatment(
                    splitTreatmentNames.videoRecallPreview,
                    getSplitUserAttributes(),
                );
                props.onFeatureLoaded(isVideoRetentionEnabled, isAutoScoreEnabled, isVideoRecallPreviewEnabled);
            } catch {
                props.onFeatureLoaded(false, false, false);
            }
        };

        checkSplit();
    }, []);

    return null;
};

const _Settings = (props: ISettingsProps) => {
    const [tabValue, setTabValue] = useState('Notifications');
    const [refreshSubscribers, setRefreshSubscribers] = useState(false);
    const [refreshEventNotifications, setRefreshEventNotifications] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [previousTabValue, setPreviousTabValue] = useState<string | null>(null);
    const [resetEventConfigSignal, setResetEventConfigSignal] = useState(0); //used to reset speed/retention configurations if the user cancels
    const [pendingNavigation, setPendingNavigation] = useState<Location | null>(null);
    const [isVideoRetentionSplitEnabled, setIsVideoRetentionSplitEnabled] = useState(false);
    const [isAutoScoreSplitEnabled, setIsAutoScoreSplitEnabled] = useState(false);
    const [isVideoRecallPreviewSplitEnabled, setIsVideoRecallPreviewSplitEnabled] = useState(false);
    const [resetVideoSettingsSignal, setResetVideoSettingsSignal] = useState(0);
    const isNavigatingProgrammatically = useRef(false);
    const [notificationsFilter, setNotificationsFilter] = useState('');
    const [subscribersFilter, setSubscribersFilter] = useState('');
    const [usersFilter, setUsersFilter] = useState('');
    const [isSearchBarFocused, setIsSearchBarFocused] = useState(false);

    const refreshSubscribersTab = useCallback((forceRefresh: boolean): void => {
        setRefreshSubscribers(forceRefresh);
    }, []);

    const refreshNotificationsTab = useCallback((forceRefresh: boolean): void => {
        setRefreshEventNotifications(forceRefresh);
    }, []);

    const refreshUserRoles = useCallback((email: string): void => {
        const userName = IntegrationPartnerDataService.getUsername().toLowerCase();
        if (userName === email.toLowerCase()) {
            IntegrationPartnerDataService.getUserRoles();
            setTabValue('Notifications');
        }
    }, []);

    const handleDirtyStateChange = useCallback((isDirty: boolean): void => {
        setHasUnsavedChanges(isDirty);
    }, []);

    const handleTabChange = useCallback(
        (event: React.ChangeEvent<object>, newTabValue: any) => {
            // If we have unsaved changes and we're leaving EventConfigurations or VideoSettings tab
            if (hasUnsavedChanges && (tabValue === 'EventConfigurations' || tabValue === 'VideoSettings')) {
                // Store the tab we're trying to navigate to
                setPreviousTabValue(newTabValue);
                setIsConfirmDialogOpen(true);
                // Don't change tabs yet - wait for dialog response
            } else {
                // No unsaved changes, proceed with tab change
                setTabValue(newTabValue);
            }
        },
        [hasUnsavedChanges, tabValue],
    );

    // Handle dialog confirmation (user wants to discard changes)
    const handleConfirmDiscard = useCallback((): void => {
        // Store navigation info locally before state update
        const navPath = pendingNavigation ? pendingNavigation.pathname : null;

        setIsConfirmDialogOpen(false);
        setHasUnsavedChanges(false);
        setTabValue(previousTabValue || 'Notifications');
        setResetEventConfigSignal((prev) => prev + 1);
        setResetVideoSettingsSignal((prev) => prev + 1);
        setPendingNavigation(null);

        if (navPath) {
            isNavigatingProgrammatically.current = true;
            props.history.push(navPath);
            isNavigatingProgrammatically.current = false;
        }
    }, [pendingNavigation, previousTabValue, props.history]);

    // Handle dialog cancelation (user wants to keep editing)
    const handleCancelNavigation = useCallback((): void => {
        setIsConfirmDialogOpen(false);
        setPreviousTabValue(null);
        setPendingNavigation(null);
    }, []);

    const handleShouldBlock = useCallback((location: Location): boolean => {
        // Skip blocking if we're navigating programmatically
        if (isNavigatingProgrammatically.current) {
            return true; // Allow navigation
        }

        // Don't prompt for tab changes within Settings
        if (location.pathname === window.location.pathname) {
            return true;
        }
        // Store exactly where we want to navigate
        setIsConfirmDialogOpen(true); //open our custom dialog
        setPendingNavigation(location);

        return false; // Prevent immediate navigation
    }, []);

    const searchTabsMap: Record<string, { value: string; setter: (v: string) => void; placeholder: string }> = {
        Notifications: { value: notificationsFilter, setter: setNotificationsFilter, placeholder: 'Search for a notification...' },
        Subscribers: { value: subscribersFilter, setter: setSubscribersFilter, placeholder: 'Search for a subscriber...' },
        Users: { value: usersFilter, setter: setUsersFilter, placeholder: 'Search for a user...' },
    };
    const activeFilter = searchTabsMap[tabValue] ?? null;

    const searchTextField = activeFilter ? (
        <TextField
            value={activeFilter.value}
            onChange={(e) => activeFilter.setter(e.target.value)}
            onFocus={() => setIsSearchBarFocused(true)}
            onBlur={() => setIsSearchBarFocused(false)}
            className={styles.searchBarInput}
            autoComplete="off"
            placeholder={activeFilter.placeholder}
            InputProps={{
                autoComplete: 'off',
                classes: {
                    root: isEncompass ? styles.encompassInputRoot : undefined,
                    focused: styles.inputFocused,
                },
                disableUnderline: isEncompass,
                startAdornment: isEncompass ? (
                    isSearchBarFocused || activeFilter.value ? null : (
                        <InputAdornment className={styles.inputAdornmentFilter} position="start">
                            <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                        </InputAdornment>
                    )
                ) : (
                    <InputAdornment className={styles.inputAdornmentFilter} position="start">
                        <FontAwesomeIcon icon={faSearch} className={isSearchBarFocused ? styles.searchIconFocused : styles.searchIcon} />
                    </InputAdornment>
                ),
                endAdornment: activeFilter.value ? (
                    <Button onClick={() => activeFilter.setter('')} className={styles.closeButtonSearchFilter}>
                        <InputAdornment className={styles.inputAdornmentCloseFilter} position="end">
                            <FontAwesomeIcon icon={faTimes} className={styles.closeIcon} />
                        </InputAdornment>
                    </Button>
                ) : null,
            }}
        />
    ) : null;

    return (
        <div
            className={
                (isAutoScoreSplitEnabled && tabValue === 'EventConfigurations') || tabValue === 'VideoSettings'
                    ? styles.mainGreyBackgroundColor
                    : styles.main
            }
        >
            <SplitFeatureWrapper
                onFeatureLoaded={(isVideoRetentionEnabled, isAutoScoreEnabled, isVideoRecallPreviewEnabled) => {
                    setIsVideoRetentionSplitEnabled(isVideoRetentionEnabled);
                    setIsAutoScoreSplitEnabled(isAutoScoreEnabled);
                    setIsVideoRecallPreviewSplitEnabled(isVideoRecallPreviewEnabled);
                }}
            />

            <NavigationPrompt when={hasUnsavedChanges} shouldBlock={handleShouldBlock} />

            <TermsOfUse />

            <PageHeader pageTitle={'Settings'} />

            <div className={styles.container}>
                <AppBar className={styles.appBar} elevation={0}>
                    <div className={styles.tabBarRow}>
                        <div className={styles.tabsWrapper}>
                            <SharedTabs
                                value={tabValue}
                                indicatorClassName={styles.tabIndicator}
                                onChange={handleTabChange}
                                flexContainerClassName={styles.flexContainer}
                            >
                                <SharedTab label="Notifications" value="Notifications" />

                                <SharedTab label="Subscribers" value="Subscribers" />

                                {(ConfigService.isIntegrationPlatformGeotab() &&
                                    canAccessUserRolesPage([RolesEnum.videoSuperAdmin]) &&
                                    isVideoRetentionSplitEnabled) ||
                                (ConfigService.isIntegrationPlatformEncompass() && canAccessUserRolesPage([RolesEnum.videoSuperAdmin])) ? (
                                    <SharedTab label="Event Configurations" value="EventConfigurations" />
                                ) : null}
                                {ConfigService.isIntegrationPlatformGeotab() && canAccessUserRolesPage([RolesEnum.videoSuperAdmin]) ? (
                                    <SharedTab label="Users" value="Users" />
                                ) : null}
                                {ConfigService.isIntegrationPlatformGeotab() &&
                                canAccessUserRolesPage([RolesEnum.videoSuperAdmin]) &&
                                isVideoRecallPreviewSplitEnabled ? (
                                    <SharedTab label="Video Settings" value="VideoSettings" />
                                ) : null}
                            </SharedTabs>
                        </div>
                        {!isEncompass && activeFilter && <div className={styles.searchInputContainer}>{searchTextField}</div>}
                    </div>
                </AppBar>

                <div className={cx([styles.tabContentContainer, tabValue !== 'Notifications' ? styles.tabContentHidden : undefined])}>
                    <EventNotificationsListDescription />
                    <EventNotificationsList
                        refreshSubscribersTab={refreshSubscribersTab}
                        refreshNotificationsTab={refreshNotificationsTab}
                        forceRefresh={refreshEventNotifications}
                        filterValue={notificationsFilter}
                        searchInput={isEncompass ? searchTextField : undefined}
                    />
                </div>

                <div className={cx([styles.tabContentContainer, tabValue !== 'Subscribers' ? styles.tabContentHidden : undefined])}>
                    <SubscribersList
                        refreshNotificationsTab={refreshNotificationsTab}
                        refreshSubscribersTab={refreshSubscribersTab}
                        forceRefresh={refreshSubscribers}
                        filterValue={subscribersFilter}
                        searchInput={isEncompass ? searchTextField : undefined}
                    />
                </div>

                <TypedSplitTreatments names={[splitTreatmentNames.videoRetention]} attributes={getSplitUserAttributes()}>
                    {({ treatments, isReady, isTimedout }: ISplitTreatmentsChildProps) => {
                        return isReady || isTimedout ? (
                            <div
                                className={cx([
                                    styles.tabContentContainer,
                                    tabValue !== 'EventConfigurations' ? styles.tabContentHidden : undefined,
                                ])}
                            >
                                {isSplitTreatmentOn(treatments[splitTreatmentNames.videoRetention]) ? (
                                    <EventConfigurations
                                        isConfirmDialogOpen={isConfirmDialogOpen}
                                        onDirtyStateChange={handleDirtyStateChange}
                                        resetSignal={resetEventConfigSignal}
                                        isAutoScoreSplitEnabled={isAutoScoreSplitEnabled}
                                    />
                                ) : (
                                    <EventConfigurations_SplitOFF />
                                )}
                            </div>
                        ) : (
                            <div></div>
                        );
                    }}
                </TypedSplitTreatments>

                {ConfigService.isIntegrationPlatformGeotab() && canAccessUserRolesPage([RolesEnum.videoSuperAdmin]) && (
                    <div className={cx([styles.tabContentContainer, tabValue !== 'Users' ? styles.tabContentHidden : undefined])}>
                        <UserRolePermissions refreshRoles={refreshUserRoles} filterValue={usersFilter} />
                    </div>
                )}

                {ConfigService.isIntegrationPlatformGeotab() &&
                    canAccessUserRolesPage([RolesEnum.videoSuperAdmin]) &&
                    isVideoRecallPreviewSplitEnabled && (
                        <div
                            className={cx([styles.tabContentContainer, tabValue !== 'VideoSettings' ? styles.tabContentHidden : undefined])}
                        >
                            <VideoOverlayTimezone onDirtyStateChange={handleDirtyStateChange} resetSignal={resetVideoSettingsSignal} />
                        </div>
                    )}
            </div>

            {/* used when there are unsaved changes and the user tries to navigate away */}
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                title={isEncompass ? 'Are you sure?' : 'Unsaved Changes'}
                contentMessage={
                    'Are you sure you want to ' + (isEncompass ? 'exit' : 'leave') + ' without saving? All of your changes will be lost.'
                }
                cancelButton={isEncompass ? 'No, Go Back' : 'Cancel'}
                acceptButton={isEncompass ? 'Yes, Exit' : 'Leave'}
                handleModalClosed={() => handleCancelNavigation()}
                handleSubmit={() => handleConfirmDiscard()}
                isAlert={isEncompass}
                alertType={AlertTypes.Alert}
                showIcon={isEncompass}
                isButtonAlert={isEncompass}
                maxWidth={isEncompass ? '504px' : '400px'}
            />
        </div>
    );
};

export const Settings = withNavigate(_Settings);
